'use client'

import { useEffect, useLayoutEffect, useState, useRef } from 'react'
import { useLoader } from '@/components/LoaderContext'

type LoadingAnimationProps = { onDone?: () => void }

/**
 * Step 1: Gate whether the intro loader should render at all.
 * - Skips when user prefers reduced motion
 * - Skips after first view in a browsing session
 *
 * Step 2 will make the loader unmount cleanly via an onDone callback.
 *
 * Renders nothing until the client has decided (useLayoutEffect) so SSR/HTML are not covered by a blank overlay.
 * When the intro runs, setLoaderActive(true) coordinates CursorReveal; otherwise the main column stays visible for LCP.
 *
 * LCP: Cap max display time so main content can paint; prevents indefinite LCP delay on slow devices.
 */
const INTRO_KEY = 'introSeen:v2'
// Must allow time for: loading + "Assets loaded" (600ms) + exit animation (1400ms)
const LOADER_MAX_DURATION_MS = 7500

function finishLoader(
  setLoaderActive: (active: boolean) => void,
  setShouldShow: (show: boolean) => void
) {
  try {
    sessionStorage.setItem(INTRO_KEY, '1')
  } catch {
    // ignore storage failures
  }
  setLoaderActive(false)
  setShouldShow(false)
}

export default function IntroLoader() {
  const [shouldShow, setShouldShow] = useState(false)
  const [hasDecided, setHasDecided] = useState(false)
  const { setLoaderActive } = useLoader()
  const maxDurationRef = useRef<number | ReturnType<typeof setTimeout> | null>(null)

  useLayoutEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const forceIntro = params.get('intro') === '1'
    const disableIntro = params.get('intro') === '0'

    const prefersReducedMotion =
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false

    let hasSeen = false
    try {
      hasSeen = sessionStorage.getItem(INTRO_KEY) === '1'
    } catch {
      hasSeen = false
    }

    const show = disableIntro ? false : forceIntro ? true : !prefersReducedMotion && !hasSeen

    setShouldShow(show)
    setHasDecided(true)
    setLoaderActive(show)
  }, [setLoaderActive])

  // Cap loader duration for LCP: force-finish so main content can paint on slow devices
  useEffect(() => {
    if (!shouldShow) return
    maxDurationRef.current = window.setTimeout(() => {
      finishLoader(setLoaderActive, setShouldShow)
      maxDurationRef.current = null
    }, LOADER_MAX_DURATION_MS)
    return () => {
      if (maxDurationRef.current) {
        window.clearTimeout(maxDurationRef.current)
        maxDurationRef.current = null
      }
    }
  }, [shouldShow, setLoaderActive])

  if (!hasDecided) return null
  if (!shouldShow) return null

  return (
    <IntroLoaderAnimation
      onDone={() => {
        if (maxDurationRef.current) {
          window.clearTimeout(maxDurationRef.current)
          maxDurationRef.current = null
        }
        finishLoader(setLoaderActive, setShouldShow)
      }}
    />
  )
}

// Load LoadingAnimation only when the intro actually shows (reduces TBT for repeat visitors)
function IntroLoaderAnimation({ onDone }: LoadingAnimationProps) {
  const [LoadingAnimation, setLoadingAnimation] = useState<React.ComponentType<LoadingAnimationProps> | null>(null)

  useEffect(() => {
    import('@/components/LoadingAnimation').then((m) => setLoadingAnimation(() => m.default))
  }, [])

  if (!LoadingAnimation) {
    return (
      <div
        className="fixed inset-0 z-[9999] bg-background dark:bg-[#161616] flex items-center justify-center"
        aria-hidden="true"
      />
    )
  }

  return <LoadingAnimation onDone={onDone} />
}

